import { useEffect, useState } from 'react';
import { Button, Col, Form, ListGroup, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import { api } from '../lib/api';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';

// 3 telas pequenas do painel antigo (tipoimovel/niveis/caracteristicas):
// lista + adicionar + excluir. Tipo/Nível em uso não podem ser excluídos
// (API responde 409 com a contagem) — o `confirm()` nativo é suficiente
// aqui, é uma ação pouco frequente numa tela de configuração.
function SecaoSimples({ rotulo, campo, listarUrl, criarUrl, chaveDados }) {
  const toast = useToast();
  const confirmar = useConfirm();
  const [itens, setItens] = useState(null);
  const [novoValor, setNovoValor] = useState('');
  const [salvando, setSalvando] = useState(false);

  function carregar() {
    api
      .get(listarUrl)
      .then((r) => setItens(r.data))
      .catch((err) => toast.erro(err.message || `Não foi possível carregar ${rotulo.toLowerCase()}s`));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(carregar, [listarUrl]);

  async function adicionar(e) {
    e.preventDefault();
    if (!novoValor.trim()) return;
    setSalvando(true);
    try {
      await api.post(criarUrl, { [campo]: novoValor.trim() });
      toast.sucesso(`${rotulo} adicionado com sucesso.`);
      setNovoValor('');
      carregar();
    } catch (err) {
      toast.erro(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(item) {
    if (!(await confirmar(`Excluir "${item[chaveDados]}"?`))) return;
    try {
      await api.delete(`${criarUrl}/${item.id}`);
      toast.sucesso(`${rotulo} excluído com sucesso.`);
      carregar();
    } catch (err) {
      toast.erro(err.message);
    }
  }

  return (
    <div className="pt-3">
      <Form onSubmit={adicionar} className="d-flex gap-2 mb-3" style={{ maxWidth: 400 }}>
        <Form.Control
          placeholder={`Novo ${rotulo.toLowerCase()}`}
          value={novoValor}
          onChange={(e) => setNovoValor(e.target.value)}
        />
        <Button type="submit" variant="primary" disabled={salvando}>
          {salvando ? <Spinner animation="border" size="sm" /> : 'Adicionar'}
        </Button>
      </Form>

      {!itens ? (
        <Spinner animation="border" />
      ) : (
        <ListGroup style={{ maxWidth: 400 }}>
          {itens.map((item) => (
            <ListGroup.Item key={item.id} className="d-flex justify-content-between align-items-center">
              <span>{item[chaveDados]}</span>
              <Button variant="link" size="sm" className="text-danger p-0" onClick={() => excluir(item)}>
                excluir
              </Button>
            </ListGroup.Item>
          ))}
          {itens.length === 0 && <ListGroup.Item className="text-muted">Nenhum cadastrado ainda.</ListGroup.Item>}
        </ListGroup>
      )}
    </div>
  );
}

function SecaoCaracteristicas() {
  const toast = useToast();
  const confirmar = useConfirm();
  const [niveis, setNiveis] = useState(null);
  const [caracteristicas, setCaracteristicas] = useState(null);
  const [descricao, setDescricao] = useState('');
  const [idNivel, setIdNivel] = useState('');
  const [salvando, setSalvando] = useState(false);

  function carregarCaracteristicas() {
    api
      .get('/caracteristicas')
      .then((r) => setCaracteristicas(r.data))
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar as características'));
  }

  useEffect(() => {
    // Achado em auditoria 2026-09-18: sem `.catch`, uma falha deixava
    // `niveis` em `null` pra sempre — o `<Form.Select>` ficava só com a
    // opção "Nível...", sem nenhum aviso de que algo deu errado (parecia
    // que só não tinha nível cadastrado, e o usuário não conseguia
    // cadastrar característica nenhuma sem entender por quê).
    api
      .get('/niveis')
      .then((r) => setNiveis(r.data))
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar os níveis'));
    carregarCaracteristicas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function adicionar(e) {
    e.preventDefault();
    if (!descricao.trim() || !idNivel) return;
    setSalvando(true);
    try {
      await api.post('/caracteristicas', { ca1_descricao: descricao.trim(), id_nivel: Number(idNivel) });
      toast.sucesso('Característica adicionada com sucesso.');
      setDescricao('');
      carregarCaracteristicas();
    } catch (err) {
      toast.erro(err.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluir(item) {
    if (!(await confirmar(`Excluir "${item.ca1_descricao}"?`))) return;
    try {
      await api.delete(`/caracteristicas/${item.id}`);
      toast.sucesso('Característica excluída com sucesso.');
      carregarCaracteristicas();
    } catch (err) {
      toast.erro(err.message);
    }
  }

  return (
    <div className="pt-3">
      <Form onSubmit={adicionar} className="mb-3" style={{ maxWidth: 500 }}>
        <Row className="g-2">
          <Col md={5}>
            <Form.Control
              placeholder="Nova característica"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
            />
          </Col>
          <Col md={4}>
            <Form.Select value={idNivel} onChange={(e) => setIdNivel(e.target.value)}>
              <option value="">Nível...</option>
              {(niveis || []).map((n) => (
                <option key={n.id} value={n.id}>
                  {n.n1_descricao}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3}>
            <Button type="submit" variant="primary" disabled={salvando} className="w-100">
              {salvando ? <Spinner animation="border" size="sm" /> : 'Adicionar'}
            </Button>
          </Col>
        </Row>
      </Form>

      {!caracteristicas ? (
        <Spinner animation="border" />
      ) : (
        <ListGroup style={{ maxWidth: 500 }}>
          {caracteristicas.map((c) => (
            <ListGroup.Item key={c.id} className="d-flex justify-content-between align-items-center">
              <span>
                {c.ca1_descricao} <span className="text-muted small">({c.n1_descricao})</span>
              </span>
              <Button variant="link" size="sm" className="text-danger p-0" onClick={() => excluir(c)}>
                excluir
              </Button>
            </ListGroup.Item>
          ))}
          {caracteristicas.length === 0 && (
            <ListGroup.Item className="text-muted">Nenhuma cadastrada ainda.</ListGroup.Item>
          )}
        </ListGroup>
      )}
    </div>
  );
}

// "Vincular Características" do legado: define QUAIS características cada tipo de
// imóvel oferece no formulário. Legado = assistente em 3 passos (tipo → nível →
// marcar); aqui é uma tela só: escolhe o tipo e marca tudo, agrupado por nível.
function SecaoVinculos() {
  const toast = useToast();
  const [tipos, setTipos] = useState(null);
  const [idTipo, setIdTipo] = useState('');
  const [grupos, setGrupos] = useState(null);
  const [marcadas, setMarcadas] = useState(new Set());
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    api
      .get('/tipos')
      .then((r) => setTipos(r.data))
      .catch((err) => toast.erro(err.message || 'Não foi possível carregar os tipos'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!idTipo) {
      setGrupos(null);
      return undefined;
    }
    let cancelado = false;
    setGrupos(null);
    api
      .get(`/tipos/${idTipo}/vinculos`)
      .then((r) => {
        if (cancelado) return;
        setGrupos(r.data);
        setMarcadas(new Set(r.data.flatMap((g) => g.itens.filter((i) => i.vinculada).map((i) => i.id))));
      })
      .catch((err) => !cancelado && toast.erro(err.message || 'Não foi possível carregar os vínculos'));
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTipo]);

  function alternar(id, ligado) {
    const novo = new Set(marcadas);
    if (ligado) novo.add(id);
    else novo.delete(id);
    setMarcadas(novo);
  }

  function alternarGrupo(grupo, ligado) {
    const novo = new Set(marcadas);
    grupo.itens.forEach((i) => (ligado ? novo.add(i.id) : novo.delete(i.id)));
    setMarcadas(novo);
  }

  async function salvar() {
    setSalvando(true);
    try {
      const r = await api.put(`/tipos/${idTipo}/vinculos`, { caracteristicas: Array.from(marcadas) });
      toast.sucesso(`Vínculos salvos (${r.adicionadas} adicionado(s), ${r.removidas} removido(s)).`);
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar os vínculos');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="pt-3">
      <Form.Group className="mb-3" style={{ maxWidth: 350 }} controlId="vinc-tipo">
        <Form.Label>Tipo de imóvel</Form.Label>
        <Form.Select value={idTipo} onChange={(e) => setIdTipo(e.target.value)}>
          <option value="">Selecione...</option>
          {(tipos || []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.t1_descricao}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      {idTipo && !grupos && <Spinner animation="border" />}
      {grupos && (
        <>
          <p className="text-muted small">
            Marque as características que este tipo de imóvel oferece. Elas aparecem na aba "Características" do formulário de
            imóvel. Desmarcar não apaga do imóvel já cadastrado; some dele na próxima vez que for salvo.
          </p>
          {grupos.map((grupo) => {
            const todas = grupo.itens.every((i) => marcadas.has(i.id));
            return (
              <div key={grupo.nivel_id} className="mb-3">
                <div className="d-flex align-items-center gap-3 border-bottom pb-1 mb-2">
                  <h6 className="mb-0">{grupo.nivel}</h6>
                  <Button variant="link" size="sm" className="p-0" onClick={() => alternarGrupo(grupo, !todas)}>
                    {todas ? 'desmarcar todas' : 'marcar todas'}
                  </Button>
                </div>
                <Row className="g-2">
                  {grupo.itens.map((item) => (
                    <Col xs={12} sm={6} md={4} lg={3} key={item.id}>
                      <Form.Check
                        type="checkbox"
                        id={`vinc-${item.id}`}
                        label={item.descricao}
                        checked={marcadas.has(item.id)}
                        onChange={(e) => alternar(item.id, e.target.checked)}
                      />
                    </Col>
                  ))}
                </Row>
              </div>
            );
          })}
          {grupos.length === 0 && <p className="text-muted">Nenhuma característica cadastrada ainda.</p>}
          <Button variant="primary" onClick={salvar} disabled={salvando}>
            {salvando ? <Spinner animation="border" size="sm" /> : 'Salvar vínculos'}
          </Button>
        </>
      )}
    </div>
  );
}

export default function Taxonomia() {
  return (
    <>
      <h1 className="h4 mb-4">Taxonomia</h1>
      <Tabs defaultActiveKey="tipos" className="mb-3">
        <Tab eventKey="tipos" title="Tipos de Imóvel">
          <SecaoSimples
            rotulo="Tipo"
            campo="t1_descricao"
            listarUrl="/tipos"
            criarUrl="/tipos"
            chaveDados="t1_descricao"
          />
        </Tab>
        <Tab eventKey="niveis" title="Níveis">
          <SecaoSimples
            rotulo="Nível"
            campo="n1_descricao"
            listarUrl="/niveis"
            criarUrl="/niveis"
            chaveDados="n1_descricao"
          />
        </Tab>
        <Tab eventKey="caracteristicas" title="Características">
          <SecaoCaracteristicas />
        </Tab>
        <Tab eventKey="vinculos" title="Vincular características">
          <SecaoVinculos />
        </Tab>
      </Tabs>
    </>
  );
}
